import React, { useState } from 'react'
import {
  Box,
  Typography,
  IconButton,
  Collapse,
  Paper,
  Tooltip
} from '@mui/material'
import {
  ExpandMore,
  ExpandLess
} from '@mui/icons-material'

interface CollapsibleSectionProps {
  title: string
  children: React.ReactNode
  defaultExpanded?: boolean
  icon?: React.ReactNode
  headerActions?: React.ReactNode
  sx?: any
  paperSx?: any
  headerSx?: any
  contentSx?: any
  showDivider?: boolean
}

const CollapsibleSection: React.FC<CollapsibleSectionProps> = ({
  title,
  children,
  defaultExpanded = true,
  icon,
  headerActions,
  sx,
  paperSx,
  headerSx,
  contentSx,
  showDivider = false
}) => {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded)

  const handleToggle = () => {
    setIsExpanded(!isExpanded)
  }

  return (
    <Box sx={sx}>
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          py: 0.75, // Reduced from 1 to 0.75
          px: showDivider ? 0 : 1.5, // Reduced from 2 to 1.5
          cursor: 'pointer',
          transition: 'background-color 0.2s ease-in-out',
          '&:hover': {
            backgroundColor: 'action.hover'
          },
          borderRadius: showDivider ? 0 : 1,
          ...headerSx
        }}
        onClick={handleToggle}
      >
        {/* Icon */}
        {icon && (
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {icon}
          </Box>
        )}

        {/* Title */}
        <Typography 
          variant="subtitle1" 
          sx={{ 
            fontSize: '0.9rem', 
            fontWeight: 600,
            flex: 1,
            userSelect: 'none'
          }}
        >
          {title}
        </Typography>

        {/* Header Actions */}
        {headerActions && (
          <Box 
            sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
            onClick={(e) => e.stopPropagation()}
          >
            {headerActions}
          </Box>
        )}

        {/* Expand/Collapse Button */}
        <Tooltip title={isExpanded ? 'Collapse section' : 'Expand section'}>
          <IconButton
            size="small"
            sx={{
              transition: 'transform 0.2s ease-in-out',
              transform: isExpanded ? 'rotate(0deg)' : 'rotate(-90deg)'
            }}
          >
            <ExpandMore sx={{ fontSize: 20 }} />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Divider */}
      {showDivider && (
        <Box
          sx={{
            height: 1,
            backgroundColor: 'divider',
            mx: 1.5, // Reduced from 2 to 1.5
            mb: isExpanded ? 1 : 0, // Reduced from 1.5 to 1
            transition: 'margin-bottom 0.3s ease-in-out'
          }}
        />
      )}

      {/* Collapsible Content */}
      <Collapse 
        in={isExpanded} 
        timeout={300}
        unmountOnExit={false}
      >
        <Box sx={contentSx}>
          {children}
        </Box>
      </Collapse>
    </Box>
  )
}

export default CollapsibleSection
